/**
 * Channels Route
 * Lists and manages workspace-wide channel integrations
 */

import { TopBarRoute, ListView } from "../../components/templates";
import {
  Stack,
  Title,
  Card,
  Icon,
  useToast,
  Modal,
  Button,
  Tag,
  Drawer
} from "@introist/react-foundation/v2";
import { useChannels, useChannelTemplates } from "../hooks/use-channels";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { channelsApi } from "../api";
import { Skeletons } from "../../components/molecules";
import { useState } from "react";
import { List, ListRow } from "../../components/organisms";
import { getPlatformIcon } from "../components/PlatformIcon";
import { getAppIcon } from "../components/AppIcon";
import { IconWithCount } from "../../foundation/blocks/IconWithCount";
import { useParams, useNavigate } from "react-router-dom";
import { ChannelEditor } from "../components/ChannelEditor";

export const ChannelsRoute = () => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { channelId } = useParams<{ channelId?: string }>();
  const navigate = useNavigate();

  return (
    <TopBarRoute
      paths={[
        {
          key: "channels",
          title: "Channels"
        }
      ]}
    >
      <Stack vertical>
        <ListView.Header title="Channels" onCreate={() => setCreateModalOpen(true)} />
        <ChannelsListContent />
        <CreateChannelModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} />
      </Stack>

      {/* Channel Editor Drawer */}
      <Drawer open={!!channelId} onClose={() => navigate("/aistudio/channels")} width="540px">
        {channelId && (
          <ChannelEditor channelId={channelId} onClose={() => navigate("/aistudio/channels")} />
        )}
      </Drawer>
    </TopBarRoute>
  );
};

const ChannelsListContent = () => {
  const { data: channels = [], isLoading } = useChannels();
  const toast = useToast();
  const navigate = useNavigate();

  const handleAuthorize = async (channelId: string) => {
    try {
      await channelsApi.initiateOAuth(channelId);
      toast.success("Authorization opened in new tab");
    } catch (error: any) {
      toast.error(`Failed to start authorization: ${error.message}`);
    }
  };

  if (isLoading) {
    return <Skeletons.List rowCount={3} rowHeight={60} />;
  }

  if (channels.length === 0) {
    return (
      <Card>
        <Stack
          vertical
          style={{
            alignItems: "center",
            gap: "var(--spacing-small)",
            padding: "var(--spacing-large)"
          }}
        >
          <Icon name="globe" dimmed />
          <Title style={{ color: "var(--palette-foreground-subdued)" }}>
            No channels configured yet
          </Title>
          <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
            Add a channel to enable agent communication
          </Title>
        </Stack>
      </Card>
    );
  }

  return (
    <List>
      {channels.map(channel => (
        <ListRow
          key={channel.id}
          gridTemplateColumns="auto 1fr auto"
          onClick={() => navigate(`/aistudio/channels/${channel.id}`)}
          clickable
        >
          <div
            style={{
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            {getAppIcon(channel.app_id)}
          </div>
          <Stack>
            <Title variant="bold">{channel.name}</Title>
            <Stack style={{ gap: "var(--spacing-xSmall)" }}>
              <Tag colorVariant="outlined" startAdornment={getPlatformIcon(channel.platform)}>
                {channel.platform}
              </Tag>
            </Stack>
          </Stack>
          <Stack>
            <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
              {channel.status === "pending_oauth" && <Tag>Pending connect</Tag>}
              {channel.status === "error" && <Tag>Error</Tag>}
            </Title>
            {channel.status === "pending_oauth" && (
              <Button
                variant="outlined"
                size="small"
                onClickWithLoading={() => handleAuthorize(channel.id)}
              >
                Authorize
              </Button>
            )}
            <IconWithCount iconName="robot" count={channel.agent_count ?? 0} hideIfZero />
          </Stack>
        </ListRow>
      ))}
    </List>
  );
};

interface CreateChannelModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateChannelModal = ({ open, onClose }: CreateChannelModalProps) => {
  const { data: templates = [], isLoading } = useChannelTemplates();
  const queryClient = useQueryClient();
  const toast = useToast();

  const createMutation = useMutation({
    mutationFn: async (template: { platform: string; app_id: string; name: string }) => {
      return channelsApi.create({
        platform: template.platform,
        app_id: template.app_id,
        name: template.name
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ai-studio", "channels"] });
      toast.success("Channel created successfully");
      onClose();
    },
    onError: (error: any) => {
      toast.error(`Failed to create channel: ${error.message}`);
    }
  });

  const handleSelectTemplate = async (template: {
    platform: string;
    app_id: string;
    name: string;
  }) => {
    await createMutation.mutateAsync(template);
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Channel">
      <Stack vertical style={{ width: "400px" }}>
        <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
          Select a channel template to create.
        </Title>

        {isLoading ? (
          <Skeletons.List rowCount={3} rowHeight={80} />
        ) : (
          <Stack vertical style={{ gap: "var(--spacing-small)" }}>
            {templates.map(template => (
              <Card
                key={template.app_id}
                onClick={() =>
                  handleSelectTemplate({
                    platform: template.platform,
                    app_id: template.app_id,
                    name: template.name
                  })
                }
                style={{ cursor: "pointer" }}
              >
                <Stack style={{ gap: "var(--spacing-medium)", alignItems: "center" }}>
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {getPlatformIcon(template.platform)}
                  </div>
                  <Stack vertical style={{ gap: "var(--spacing-xxSmall)", flex: 1 }}>
                    <Title variant="bold">{template.name}</Title>
                    {template.description && (
                      <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
                        {template.description}
                      </Title>
                    )}
                  </Stack>
                  <Icon name="chevronRight" dimmed />
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};
